import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/joy";
import {
  CheckCircle,
  FileText,
  HouseLine,
  CalendarBlank,
  ArrowSquareOut,
} from "@phosphor-icons/react";
import type {
  HomeownerProjectData,
  HomeownerPublication,
} from "../../types/homeowner";
const date = (value?: string | null) =>
  value
    ? new Date(
        value.length === 10 ? `${value}T12:00:00` : value,
      ).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
    : "Not scheduled";
const money = (value?: string) =>
  Number(value || 0).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
const readable = (value?: string) => (value || "").replaceAll("_", " ");

function SharedAsset({
  item,
  loadFile,
}: {
  item: HomeownerPublication;
  loadFile: (item: HomeownerPublication) => Promise<Blob>;
}) {
  const [image, setImage] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let url: string | null = null;
    if (item.kind === "photo")
      void loadFile(item)
        .then((blob) => {
          if (cancelled) return;
          url = URL.createObjectURL(blob);
          setImage(url);
        })
        .catch(() => {
          if (!cancelled) setError("This photo is no longer available.");
        });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [item.id, loadFile]);
  const download = async () => {
    setBusy(true);
    try {
      const url = URL.createObjectURL(await loadFile(item));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = item.filename || `${item.title}.pdf`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Unable to download this file.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card variant="outlined" sx={{ minWidth: 0, boxShadow: "none" }}>
      {item.kind === "photo" && image && (
        <Box
          component="img"
          src={image}
          alt={item.title}
          sx={{
            width: "100%",
            height: 240,
            objectFit: "cover",
            borderRadius: "sm",
          }}
        />
      )}
      <Typography level="title-lg">{item.title}</Typography>
      <Chip size="sm" variant="soft">
        {readable(item.kind)}
      </Chip>
      {item.note && (
        <Typography sx={{ whiteSpace: "pre-wrap" }}>{item.note}</Typography>
      )}
      <Typography level="body-xs">Shared {date(item.published_at)}</Typography>
      <Button
        variant="outlined"
        onClick={() => void download()}
        loading={busy}
        startDecorator={<FileText />}
      >
        Download {item.kind === "photo" ? "photo" : "document"}
      </Button>
      {error && <Alert color="warning">{error}</Alert>}
    </Card>
  );
}

export default function ProjectView({
  data,
  loadFile,
  children,
}: {
  data: HomeownerProjectData;
  loadFile: (item: HomeownerPublication) => Promise<Blob>;
  children?: React.ReactNode;
}) {
  const brand = data.company.brand;
  const stages = ["planning", "scheduled", "in_progress", "completed"];
  const current = stages.indexOf(data.phase);
  const files = data.publications.filter((row) => row.file_url);
  const updates = data.publications.filter((row) =>
    ["update", "appointment"].includes(row.kind),
  );
  const invoices = data.publications.filter((row) => row.kind === "invoice");
  const actions = data.publications.filter(
    (row) =>
      ["proposal", "contract", "maintenance"].includes(row.kind) &&
      !row.file_url,
  );
  return (
    <Box
      data-testid="homeowner-project"
      sx={{
        "--joy-palette-text-primary": brand.ink,
        "--joy-palette-text-secondary": brand.muted,
        "--joy-palette-background-surface": brand.panel,
        "--joy-palette-background-level1": brand.raised,
        "--joy-palette-background-level2": brand.soft,
        "--joy-palette-primary-solidBg": brand.strong,
        "--joy-palette-primary-solidHoverBg": brand.deep,
        "--joy-palette-primary-outlinedColor": brand.accent,
        bgcolor: brand.canvas,
        color: brand.ink,
        borderRadius: "xl",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 5 },
          py: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid",
          borderColor: brand.line,
        }}
      >
        {data.company.logo ? (
          <Box
            component="img"
            src={data.company.logo}
            alt={data.company.name}
            sx={{ maxWidth: 180, maxHeight: 60, objectFit: "contain" }}
          />
        ) : (
          <HouseLine size={32} color={brand.accent} />
        )}
        <Box>
          <Typography level="title-lg">{data.company.name}</Typography>
          <Typography level="body-sm">
            Your project & permanent roof record
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          px: { xs: 2, md: 5 },
          py: { xs: 3, md: 5 },
          background: `linear-gradient(120deg, ${brand.strong}, ${brand.deep})`,
          color: brand.on_brand,
        }}
      >
        <Typography level="body-sm" sx={{ color: "inherit", mb: 1 }}>
          HOMEOWNER PROJECT HUB
        </Typography>
        <Typography
          level="h1"
          sx={{
            color: "inherit",
            fontSize: { xs: "2rem", md: "2.7rem" },
            maxWidth: 900,
          }}
        >
          {data.title}
        </Typography>
        <Typography sx={{ color: "inherit", mt: 1 }}>{data.address}</Typography>
        {data.introduction && (
          <Typography
            sx={{
              color: "inherit",
              mt: 3,
              maxWidth: 850,
              whiteSpace: "pre-wrap",
            }}
          >
            {data.introduction}
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 3 }}>
          {stages.map((stage, index) => (
            <Chip
              key={stage}
              startDecorator={
                index < current ? <CheckCircle weight="fill" /> : null
              }
              sx={{
                bgcolor: index === current ? "#fff" : "rgba(255,255,255,.12)",
                color: index === current ? brand.deep : "#fff",
                borderColor: "transparent",
              }}
            >
              {readable(stage)}
            </Chip>
          ))}
        </Box>
      </Box>
      <Box sx={{ p: { xs: 2, md: 5 }, display: "grid", gap: 4 }}>
        {data.next_update && (
          <Card variant="soft" sx={{ bgcolor: brand.raised }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <CalendarBlank size={24} />
              <Box>
                <Typography level="title-md">
                  Your next project update
                </Typography>
                <Typography>{date(data.next_update)}</Typography>
              </Box>
            </Stack>
          </Card>
        )}
        <Box>
          <Typography level="h2" sx={{ fontSize: "1.5rem", mb: 2 }}>
            Project updates
          </Typography>
          {updates.length ? (
            <Stack spacing={2}>
              {updates.map((row) => (
                <Card
                  key={row.id}
                  variant="outlined"
                  sx={{ boxShadow: "none" }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="sm">
                      {row.kind === "appointment"
                        ? readable(row.status)
                        : "Project update"}
                    </Chip>
                    <Typography level="body-sm">{date(row.date)}</Typography>
                  </Stack>
                  <Typography level="title-lg">{row.title}</Typography>
                  {row.start && (
                    <Typography>
                      {row.start.slice(0, 5)}
                      {row.end ? ` – ${row.end.slice(0, 5)}` : ""}
                    </Typography>
                  )}
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {row.note}
                  </Typography>
                </Card>
              ))}
            </Stack>
          ) : (
            <Typography>
              Your contractor will publish updates here as the work progresses.
            </Typography>
          )}
        </Box>
        <Card sx={{ bgcolor: brand.raised, boxShadow: "none" }}>
          <Typography level="h2" sx={{ fontSize: "1.5rem" }}>
            Your roof record
          </Typography>
          <Typography level="body-sm">
            Installation information and care instructions, kept with your home.
          </Typography>
          <Divider />
          <Typography level="title-sm">Installation date</Typography>
          <Typography>
            {data.roof_record.installed_on
              ? date(data.roof_record.installed_on)
              : "Your contractor has not recorded the installation date yet."}
          </Typography>
          {data.roof_record.materials && (
            <>
              <Typography level="title-sm">Materials installed</Typography>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {data.roof_record.materials}
              </Typography>
            </>
          )}
          {data.roof_record.care_notes && (
            <>
              <Typography level="title-sm">Care & service</Typography>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>
                {data.roof_record.care_notes}
              </Typography>
            </>
          )}
          <Typography level="body-xs">
            Warranty terms are defined by the warranty documents shared below.
          </Typography>
        </Card>
        {!!actions.length && (
          <Box>
            <Typography level="h2" sx={{ fontSize: "1.5rem", mb: 2 }}>
              Agreements & next steps
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              {actions.map((row) => (
                <Card
                  key={row.id}
                  variant="outlined"
                  sx={{ boxShadow: "none" }}
                >
                  <Chip size="sm">
                    {readable(row.purpose || row.kind)} · {readable(row.status)}
                  </Chip>
                  <Typography level="title-lg">{row.title}</Typography>
                  <Typography sx={{ whiteSpace: "pre-wrap" }}>
                    {row.note}
                  </Typography>
                  {row.renewal_end && (
                    <Typography>
                      Renewal through {date(row.renewal_end)} ·{" "}
                      {money(row.renewal_fee)} per visit
                    </Typography>
                  )}
                  {row.kind === "maintenance" && (
                    <Typography>
                      Coverage dates: {date(row.start)} –{" "}
                      {row.end ? date(row.end) : "Ongoing"}
                    </Typography>
                  )}
                  {row.current === false && (
                    <Alert color="warning">
                      This offer is being updated. Your contractor will share
                      the reviewed version.
                    </Alert>
                  )}
                  {row.action_url && (
                    <Button
                      component="a"
                      href={row.action_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      endDecorator={<ArrowSquareOut />}
                    >
                      {row.status === "accepted"
                        ? "View accepted proposal"
                        : row.kind === "contract"
                          ? "Open contract signing"
                          : "Review proposal"}
                    </Button>
                  )}
                </Card>
              ))}
            </Box>
          </Box>
        )}
        {!!files.length && (
          <Box>
            <Typography level="h2" sx={{ fontSize: "1.5rem", mb: 2 }}>
              Photos & documents
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2,
              }}
            >
              {files.map((item) => (
                <SharedAsset key={item.id} item={item} loadFile={loadFile} />
              ))}
            </Box>
          </Box>
        )}
        {!!invoices.length && (
          <Box>
            <Typography level="h2" sx={{ fontSize: "1.5rem", mb: 2 }}>
              Invoices
            </Typography>
            <Stack spacing={2}>
              {invoices.map((row) => (
                <Card
                  key={row.id}
                  variant="outlined"
                  sx={{ boxShadow: "none" }}
                >
                  <Typography level="title-lg">{row.title}</Typography>
                  <Typography level="h3">
                    {money(row.balance)}{" "}
                    <Typography level="body-sm">recorded balance</Typography>
                  </Typography>
                  <Typography>
                    Total {money(row.total)}
                    {row.due ? ` · Due ${date(row.due)}` : ""}
                  </Typography>
                  <Typography level="body-xs">
                    Payments appear after your contractor records them.
                  </Typography>
                </Card>
              ))}
            </Stack>
            {data.company.payment_url &&
              invoices.some((row) => Number(row.balance) > 0) && (
                <Button
                  sx={{ mt: 2 }}
                  component="a"
                  href={data.company.payment_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  endDecorator={<ArrowSquareOut />}
                >
                  Open contractor payment page
                </Button>
              )}
          </Box>
        )}
        {children}
        <Typography level="body-xs" sx={{ textAlign: "center" }}>
          Shared privately by {data.company.name}
          {data.company.phone ? ` · ${data.company.phone}` : ""}. Powered by
          ApexOS.
        </Typography>
      </Box>
    </Box>
  );
}
